/** @file Grab item: drag a mounted item through the IK that ends at its socket's parent bone. */
import type { Id } from '@pose2d/format/types';
import { localValue } from '../../panels/viewport/scene/localValue';
import type { ToolContext, ToolPointer } from '../../panels/viewport/viewportTypes';
import { chainRotations } from '../transform/chainRotations';
import { editTargetOf, type EditTarget } from '../transform/editTargetOf';
import type { IkRef } from '../transform/ikRef';
import { ikTargetGesture, type IkExtras } from '../transform/ikTargetGesture';
import { ikEndingAt } from './ikEndingAt';
import { itemAt } from './itemAt';

/** Setup: every chain bone's solved local rotation; Animate: the wrist's local rotation key (raw, R-E1-06). */
function grabExtras(ctx: ToolContext, edit: EditTarget, wrist: Id, ik: IkRef): IkExtras {
  if (edit.mode === 'setup') return chainRotations(ctx, ik);
  const i = ctx.maps.boneIndex.get(wrist);
  return (now) => (i === undefined ? new Map() : new Map([[wrist, { rotation: localValue(now.pose, i, 'rotation') }]]));
}

/** R-E1-12: no item under the press -> notice; no IK ending at the socket parent -> the Suggest IK offer. */
export const grabItemPointer: ToolPointer = {
  press(ctx, p) {
    const wrist = itemAt(ctx, p);
    if (wrist === null) {
      ctx.notify({ kind: 'info', key: 'viewport.noItem' });
      return null;
    }
    const ik = ikEndingAt(ctx, wrist);
    if (ik === null) {
      ctx.notify({ kind: 'offer', key: 'viewport.noIk', params: { bone: ctx.doc.bones[wrist]?.name ?? wrist }, action: 'suggestIk' });
      return null;
    }
    const edit = editTargetOf(ctx);
    return edit === null ? null : ikTargetGesture(ctx, edit, ik, p, grabExtras(ctx, edit, wrist, ik));
  },
  idle: () => null,
};
